import PrimaryButtonClicky from "@/app/_components/PrimaryButtonClicky";
import { useRef } from "react";

interface VerificationCodeStepProps {
  maskedEmail: string;
  verificationCode: string;
  onCodeChange: (code: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onResend: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  timeRemaining: number | null;
  resendCooldown: number | null;
  formatTime: (seconds: number) => string;
}

export default function VerificationCodeStep({
  maskedEmail,
  verificationCode,
  onCodeChange,
  onSubmit,
  onResend,
  onBack,
  isLoading,
  error,
  timeRemaining,
  resendCooldown,
  formatTime,
}: VerificationCodeStepProps) {
  const codeFormRef = useRef<HTMLFormElement>(null);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Check your email</h2>
        <p className="text-gray-600 text-sm">
          We sent a verification code to{" "}
          <span className="font-medium">{maskedEmail}</span>
        </p>
      </div>

      <form ref={codeFormRef} onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="code" className="text-sm font-medium">
            Verification Code
          </label>
          <input
            id="code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            required
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ""))}
            autoFocus
            className="w-full py-4 border rounded-2xl text-center bg-[#F9F9FB] focus:outline-[#05B0FF] font-medium tracking-[0.5em] text-lg"
          />
        </div>

        {timeRemaining !== null && timeRemaining > 0 && (
          <p className="text-sm text-gray-500 text-center">
            Code expires in {formatTime(timeRemaining)}
          </p>
        )}

        {error && (
          <div className="text-red-500 text-sm text-center">{error}</div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="hidden"
          aria-hidden="true"
        />
        <div
          onClick={() => {
            if (codeFormRef.current && !isLoading) {
              codeFormRef.current.requestSubmit();
            }
          }}
        >
          <PrimaryButtonClicky
            buttonText="Verify & Sign Up"
            disabled={isLoading || verificationCode.length !== 6}
            isLoading={isLoading}
            doesStretch={true}
            showKeyboardShortcut={false}
            buttonState="filled"
          />
        </div>
      </form>

      <div className="flex flex-col items-center gap-2">
        {resendCooldown !== null && resendCooldown > 0 ? (
          <p className="text-sm text-gray-400">
            Resend code in {formatTime(resendCooldown)}
          </p>
        ) : (
          <button
            type="button"
            onClick={onResend}
            disabled={isLoading}
            className="text-sm text-[#05B0FF] hover:underline disabled:opacity-50"
          >
            Resend code
          </button>
        )}
        <button
          type="button"
          onClick={onBack}
          disabled={isLoading}
          className="text-sm text-gray-500 hover:underline disabled:opacity-50"
        >
          Back to edit email
        </button>
      </div>
    </div>
  );
}
